import * as amplitude from "@amplitude/analytics-browser";
import { Flex, IconButton, Text } from "@chakra-ui/react";
import React from "react";
import {
  RiAddLine,
  RiArrowDownSLine,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiArrowUpSLine,
  RiDownloadLine,
  RiSubtractLine,
} from "react-icons/ri";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { triggerDownload } from "src/services/dataTransfer";
import {
  DocumentLoaded,
  DocumentLoadedEvent,
  ZoomChanged,
  ZoomChangedEvent,
} from "./CustomEvents";
import { DocumentViewerElement } from "./DocumentViewerElement";
import { Document } from "./types";

type Props = {
  currentDocument: Document;
  documentViewerRef: React.RefObject<DocumentViewerElement | undefined>;
};
export const DocumentViewerToolbar = ({
  currentDocument: document,
  documentViewerRef,
}: Props) => {
  const [scale, setScale] = React.useState(1);
  const toast = useAvelaToast();
  const download = React.useCallback(async (): Promise<void> => {
    let url: string;
    switch (document.type) {
      case "base64": {
        url = document.base64;
        break;
      }

      case "url": {
        url = document.url;
        break;
      }
    }

    const response = await fetch(url);
    if (response.status !== 200) {
      console.error("Unable to download document:", response.statusText);
      toast.error({ title: "Unable to download document" });
      return;
    }

    const blob = await response.blob();
    triggerDownload(blob, document.fileName);
    amplitude.track("File downloaded");
  }, [document, toast]);

  const onZoomChanged = React.useCallback((event: ZoomChangedEvent) => {
    setScale(event.detail);
  }, []);

  const [numberOfPages, setNumberOfPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const hasMultiplePages = numberOfPages > 1;
  const onDocumentLoaded = React.useCallback((event: DocumentLoadedEvent) => {
    switch (event.detail.documentType) {
      case "pdf": {
        setNumberOfPages(event.detail.numberOfPages);
        break;
      }
      case "image": {
        setNumberOfPages(1);
        break;
      }
    }
  }, []);

  React.useEffect(() => {
    const documentViewer = documentViewerRef.current;
    documentViewer?.addEventListener(ZoomChanged, onZoomChanged);
    documentViewer?.addEventListener(DocumentLoaded, onDocumentLoaded);
    return () => {
      documentViewer?.removeEventListener(ZoomChanged, onZoomChanged);
      documentViewer?.removeEventListener(DocumentLoaded, onDocumentLoaded);
    };
  }, [documentViewerRef, onDocumentLoaded, onZoomChanged]);

  return (
    <Flex
      position="absolute"
      bottom="0"
      paddingBottom="4"
      left="0"
      width="100%"
      justifyContent="center"
      overflow="auto"
      sx={{
        containerType: "inline-size",
        "@container (width < 450px)": {
          paddingBottom: 0,
        },
      }}
    >
      <Flex
        bg="gray.50"
        padding="2"
        rounded="md"
        shadow="md"
        gap="4"
        sx={{
          "@container (width < 450px)": {
            gap: "2",
            rounded: 0,
            width: "100%",
          },
        }}
        flexWrap="wrap"
        justifyContent="center"
      >
        {hasMultiplePages ? (
          <Flex
            gap="2"
            alignItems="center"
            paddingRight="4"
            sx={{
              "@container (width < 450px)": {
                paddingRight: "2",
              },
            }}
            borderRight="1px solid"
            borderColor="gray.200"
          >
            <IconButton
              icon={<RiArrowUpSLine />}
              aria-label="Previous page"
              variant="outline"
              colorScheme="gray"
              onClick={async () => {
                if (!documentViewerRef.current) {
                  return;
                }

                const { pageNumber } =
                  await documentViewerRef.current.previousPage();
                setCurrentPage(pageNumber);
                amplitude.track("Page changed", {
                  page_number: pageNumber,
                });
              }}
            />
            {hasMultiplePages ? (
              <Text>
                Page {currentPage} of {numberOfPages}
              </Text>
            ) : null}
            <IconButton
              icon={<RiArrowDownSLine />}
              aria-label="Next page"
              variant="outline"
              colorScheme="gray"
              onClick={async () => {
                if (!documentViewerRef.current) {
                  return;
                }

                const { pageNumber } =
                  await documentViewerRef.current.nextPage();
                setCurrentPage(pageNumber);
                amplitude.track("Page changed", {
                  page_number: pageNumber,
                });
              }}
            />
          </Flex>
        ) : null}
        <Flex
          gap="2"
          alignItems="center"
          paddingRight="4"
          sx={{
            "@container (width < 450px)": {
              paddingRight: "2",
            },
          }}
          borderRight="1px solid"
          borderColor="gray.200"
        >
          <IconButton
            icon={<RiSubtractLine />}
            aria-label="Zoom out"
            variant="outline"
            colorScheme="gray"
            size="sm"
            sx={{
              "@container (width < 450px)": {
                minWidth: "10",
                minHeight: "10",
              },
            }}
            onClick={() => {
              if (!documentViewerRef.current) return;
              const newScale = documentViewerRef.current.zoomOut();
              setScale(newScale);
              amplitude.track("File zoomed out", { source: "button" });
            }}
          />
          <Text
            sx={{
              "@container (width < 450px)": {
                display: "none",
              },
            }}
          >
            {Math.round(scale * 100)}%
          </Text>
          <IconButton
            icon={<RiAddLine />}
            aria-label="Zoom in"
            variant="outline"
            colorScheme="gray"
            size="sm"
            sx={{
              "@container (width < 450px)": {
                minWidth: "10",
                minHeight: "10",
              },
            }}
            onClick={() => {
              if (!documentViewerRef.current) return;
              const newScale = documentViewerRef.current.zoomIn();
              setScale(newScale);
              amplitude.track("File zoomed in", { source: "button" });
            }}
          />
        </Flex>
        <Flex gap="2" alignItems="center">
          <IconButton
            icon={<RiArrowGoBackLine />}
            aria-label="Rotate left"
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              documentViewerRef.current?.rotateLeft();
              amplitude.track("File turned");
            }}
          />
          <IconButton
            icon={<RiArrowGoForwardLine />}
            aria-label="Rotate right"
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              documentViewerRef.current?.rotateRight();
              amplitude.track("File turned");
            }}
          />
          <IconButton
            icon={<RiDownloadLine />}
            aria-label="Download"
            variant="outline"
            colorScheme="gray"
            onClick={download}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
